import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Tab
} from '@mui/material'
import {
  Restaurant,
  Logout,
  PictureAsPdf,
  TableChart,
  Search
} from '@mui/icons-material'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/axios'

const formatNumeroBR = (v) => (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const formatBRL = (v) => `R$ ${formatNumeroBR(v)}`
const hoje = () => new Date().toISOString().slice(0, 10)

// Entidade "fornecedor" de um item: o grupo/associação do produtor, se houver, senão o próprio produtor.
const fornecedorDoItem = (item) => {
  if (item.idgrupo) {
    return { key: `grupo:${item.idgrupo}`, nome: item.nome_grupo || item.produtor }
  }
  return { key: `produtor:${item.produtor_id}`, nome: item.produtor }
}

export default function Relatorios() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [aba, setAba] = useState(0)
  const [error, setError] = useState('')

  // Aba 1: Cadastro de produtores
  const [produtores, setProdutores] = useState([])
  const [filtroNome, setFiltroNome] = useState('')
  const [loadingProdutores, setLoadingProdutores] = useState(false)

  // Editais (compartilhado pelas abas 2 e 3)
  const [editais, setEditais] = useState([])
  const [editalId, setEditalId] = useState('')
  const [fornecimentoData, setFornecimentoData] = useState(null)
  const [loadingFornecimento, setLoadingFornecimento] = useState(false)

  const logoutAdmin = () => {
    logout()
    navigate('/admin/login')
  }

  const carregarProdutores = async (nome) => {
    setLoadingProdutores(true)
    setError('')
    try {
      const res = await api.put('/relatorios/produtores.php', { nome: nome || '' })
      if (res.data.success) setProdutores(res.data.produtores || [])
      else setError(res.data.message || 'Erro ao carregar produtores')
    } catch (e) {
      setError(e.response?.data?.message || 'Erro ao carregar produtores')
    } finally {
      setLoadingProdutores(false)
    }
  }

  useEffect(() => { carregarProdutores('') }, [])

  useEffect(() => {
    const run = async () => {
      try {
        const res = await api.get('/editais/list.php')
        if (res.data.success) setEditais(res.data.editais || [])
      } catch {
        setError('Erro ao carregar editais.')
      }
    }
    run()
  }, [])

  useEffect(() => {
    if (!editalId) {
      setFornecimentoData(null)
      return
    }
    const run = async () => {
      setLoadingFornecimento(true)
      setError('')
      try {
        const res = await api.get(`/relatorios/fornecimento.php?edital_id=${editalId}`)
        if (res.data.success) setFornecimentoData(res.data)
        else setError(res.data.message || 'Erro ao carregar fornecimento do edital')
      } catch (e) {
        setError(e.response?.data?.message || 'Erro ao carregar fornecimento do edital')
      } finally {
        setLoadingFornecimento(false)
      }
    }
    run()
  }, [editalId])

  const editalSelecionado = useMemo(
    () => editais.find((e) => String(e.id) === String(editalId)) || fornecimentoData?.edital || null,
    [editais, editalId, fornecimentoData]
  )

  // Pivot: fornecedor (linha) x produto (coluna) = quantidade somada
  const detalhamentoPivot = useMemo(() => {
    const itens = fornecimentoData?.itens || []
    const produtosSet = new Map()
    const fornecedoresMap = new Map()
    const matrix = {}

    itens.forEach((item) => {
      const { key, nome } = fornecedorDoItem(item)
      fornecedoresMap.set(key, nome)
      produtosSet.set(item.produto, true)
      if (!matrix[key]) matrix[key] = {}
      matrix[key][item.produto] = (matrix[key][item.produto] || 0) + (Number(item.quantidade) || 0)
    })

    const produtos = Array.from(produtosSet.keys()).sort((a, b) => a.localeCompare(b, 'pt-BR'))
    const fornecedores = Array.from(fornecedoresMap.entries())
      .map(([key, nome]) => ({ key, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

    return { produtos, fornecedores, matrix }
  }, [fornecimentoData])

  // Pivot: produto (linha) x fornecedor (par de colunas Kg/R$)
  const resumoPivot = useMemo(() => {
    const itens = fornecimentoData?.itens || []
    const produtosMap = new Map()
    const fornecedoresMap = new Map()
    const matrix = {}
    const totalPorFornecedor = {}

    itens.forEach((item) => {
      const { key, nome } = fornecedorDoItem(item)
      fornecedoresMap.set(key, nome)
      const qtd = Number(item.quantidade) || 0
      const valor = Number(item.valor_total) || 0

      produtosMap.set(item.produto, (produtosMap.get(item.produto) || 0) + qtd)

      if (!matrix[key]) matrix[key] = {}
      if (!matrix[key][item.produto]) matrix[key][item.produto] = { kg: 0, valor: 0 }
      matrix[key][item.produto].kg += qtd
      matrix[key][item.produto].valor += valor

      totalPorFornecedor[key] = (totalPorFornecedor[key] || 0) + valor
    })

    const produtos = Array.from(produtosMap.entries())
      .map(([nome, totalKg]) => ({ nome, totalKg }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    const fornecedores = Array.from(fornecedoresMap.entries())
      .map(([key, nome]) => ({ key, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

    const totalGeral = Object.values(totalPorFornecedor).reduce((s, v) => s + v, 0)

    return { produtos, fornecedores, matrix, totalPorFornecedor, totalGeral }
  }, [fornecimentoData])

  // ---------- Exportações: Aba 1 (Produtores) ----------

  const colunasProdutores = [
    { header: 'CPF', get: (p) => p.cpf || '' },
    { header: 'Nome', get: (p) => p.nome || '' },
    { header: 'Nascimento', get: (p) => p.dtnascimento || '' },
    { header: 'Gênero', get: (p) => p.genero || '' },
    { header: 'Apelido', get: (p) => p.apelido || '' },
    { header: 'Cônjuge', get: (p) => p.nmconjuge || '' },
    { header: 'Telefone', get: (p) => p.telefone || '' },
    { header: 'Sítio/Propriedade', get: (p) => p.sitiopropriedade || '' },
    { header: 'Localidade/Referência', get: (p) => p.localidadereferencia || '' },
    { header: 'Ponto de Georreferenciamento', get: (p) => p.pontogeoreferencia || '' },
    { header: 'Área Aproximada da Propriedade', get: (p) => p.areapropriedade || '' },
    { header: 'Grupo/Associação', get: (p) => p.nome_grupo || '' },
    { header: 'Gêneros Alimentícios Produzidos', get: (p) => p.generos_produzidos || '' }
  ]

  const exportarProdutoresExcel = () => {
    const header = colunasProdutores.map((c) => c.header)
    const rows = produtores.map((p) => colunasProdutores.map((c) => c.get(p)))
    const aoa = [
      ['FORMULÁRIO GERAL DE CADASTRO DE PRODUTORES AGRÍCOLAS'],
      [],
      header,
      ...rows
    ]
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } }]
    ws['!cols'] = header.map(() => ({ wch: 22 }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Produtores')
    XLSX.writeFile(wb, `cadastro_produtores_${hoje()}.xlsx`)
  }

  const exportarProdutoresPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' })
    doc.setFontSize(12)
    doc.text('Cadastro de Produtores', 14, 12)
    autoTable(doc, {
      startY: 18,
      styles: { fontSize: 6.5 },
      head: [colunasProdutores.map((c) => c.header)],
      body: produtores.map((p) => colunasProdutores.map((c) => c.get(p)))
    })
    doc.save(`cadastro_produtores_${hoje()}.pdf`)
  }

  // ---------- Exportações: Aba 2 (Detalhamento do fornecimento) ----------

  const tituloEdital = editalSelecionado?.titulo || ''

  const exportarDetalhamentoExcel = () => {
    const { produtos, fornecedores, matrix } = detalhamentoPivot
    const aoa = []
    aoa.push([`Detalhamento do fornecimento dos gêneros da agricultura familiar - ${tituloEdital}`])
    aoa.push(['', 'Quantidades fornecidas'])
    aoa.push(['Grupo ou agricultor', ...produtos])
    fornecedores.forEach((f) => {
      aoa.push([f.nome, ...produtos.map((p) => (matrix[f.key]?.[p] ? matrix[f.key][p] : ''))])
    })

    const ws = XLSX.utils.aoa_to_sheet(aoa)
    const lastCol = produtos.length
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
      { s: { r: 1, c: 1 }, e: { r: 1, c: lastCol } }
    ]
    ws['!cols'] = [{ wch: 32 }, ...produtos.map(() => ({ wch: 12 }))]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Detalhamento')
    XLSX.writeFile(wb, `detalhamento_fornecimento_edital_${editalId}.xlsx`)
  }

  const exportarDetalhamentoPdf = () => {
    const { produtos, fornecedores, matrix } = detalhamentoPivot
    const doc = new jsPDF({ orientation: 'landscape' })
    doc.setFontSize(11)
    doc.text(`Detalhamento do fornecimento - ${tituloEdital}`, 14, 12)
    autoTable(doc, {
      startY: 18,
      styles: { fontSize: 7 },
      head: [['Grupo ou agricultor', ...produtos]],
      body: fornecedores.map((f) => [f.nome, ...produtos.map((p) => (matrix[f.key]?.[p] ? formatNumeroBR(matrix[f.key][p]) : ''))])
    })
    doc.save(`detalhamento_fornecimento_edital_${editalId}.pdf`)
  }

  // ---------- Exportações: Aba 3 (Resumo para pagamento de NFs) ----------

  const exportarResumoExcel = () => {
    const { produtos, fornecedores, matrix, totalPorFornecedor } = resumoPivot
    const aoa = []
    aoa.push([`Resumo para pagamento de NFs da Agricultura Familiar - ${tituloEdital}`])
    aoa.push([])

    const headerRow1 = ['Gênero', 'Total Fornecido (Kg)']
    fornecedores.forEach((f) => { headerRow1.push(f.nome); headerRow1.push('') })
    aoa.push(headerRow1)

    const headerRow2 = ['', '']
    fornecedores.forEach(() => { headerRow2.push('Kg'); headerRow2.push('R$') })
    aoa.push(headerRow2)

    produtos.forEach((p) => {
      const row = [p.nome, formatNumeroBR(p.totalKg)]
      fornecedores.forEach((f) => {
        const cell = matrix[f.key]?.[p.nome]
        row.push(cell ? formatNumeroBR(cell.kg) : '')
        row.push(cell ? cell.valor.toFixed(2) : '0.00')
      })
      aoa.push(row)
    })

    aoa.push([])
    const totalRow = ['Total NF', '']
    fornecedores.forEach((f) => { totalRow.push(''); totalRow.push(formatBRL(totalPorFornecedor[f.key] || 0)) })
    aoa.push(totalRow)

    const nfRow = ['', '']
    fornecedores.forEach(() => { nfRow.push('N° NF'); nfRow.push('') })
    aoa.push(nfRow)

    const processoRow = ['', '']
    fornecedores.forEach(() => { processoRow.push('Processo n°'); processoRow.push('') })
    aoa.push(processoRow)

    const ws = XLSX.utils.aoa_to_sheet(aoa)
    const lastCol = 1 + fornecedores.length * 2
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } }]
    fornecedores.forEach((f, idx) => {
      const col = 2 + idx * 2
      ws['!merges'].push({ s: { r: 2, c: col }, e: { r: 2, c: col + 1 } })
    })
    ws['!cols'] = [{ wch: 22 }, { wch: 14 }, ...fornecedores.flatMap(() => [{ wch: 10 }, { wch: 12 }])]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Resumo NFs')
    XLSX.writeFile(wb, `resumo_pagamento_nfs_edital_${editalId}.xlsx`)
  }

  const exportarResumoPdf = () => {
    const { produtos, fornecedores, matrix, totalPorFornecedor } = resumoPivot
    const doc = new jsPDF({ orientation: 'landscape' })
    doc.setFontSize(10)
    doc.text(`Resumo para pagamento de NFs - ${tituloEdital}`, 14, 10)

    const head = [
      [
        { content: 'Gênero', rowSpan: 2 },
        { content: 'Total (Kg)', rowSpan: 2 },
        ...fornecedores.map((f) => ({ content: f.nome, colSpan: 2 }))
      ],
      [
        ...fornecedores.flatMap(() => [{ content: 'Kg' }, { content: 'R$' }])
      ]
    ]

    const body = produtos.map((p) => {
      const row = [p.nome, formatNumeroBR(p.totalKg)]
      fornecedores.forEach((f) => {
        const cell = matrix[f.key]?.[p.nome]
        row.push(cell ? formatNumeroBR(cell.kg) : '')
        row.push(cell ? formatBRL(cell.valor) : formatBRL(0))
      })
      return row
    })

    const totalRow = ['Total NF', '']
    fornecedores.forEach((f) => { totalRow.push(''); totalRow.push(formatBRL(totalPorFornecedor[f.key] || 0)) })
    body.push(totalRow)

    autoTable(doc, {
      startY: 16,
      styles: { fontSize: 6 },
      head,
      body
    })
    doc.save(`resumo_pagamento_nfs_edital_${editalId}.pdf`)
  }

  const seletorEdital = (
    <FormControl size="small" sx={{ minWidth: 320, mb: 2 }}>
      <InputLabel id="edital-relatorio-label" shrink>Edital de referência</InputLabel>
      <Select
        labelId="edital-relatorio-label"
        label="Edital de referência"
        value={editalId}
        onChange={(e) => setEditalId(e.target.value)}
        displayEmpty
      >
        <MenuItem value=""><em>Selecione um edital</em></MenuItem>
        {editais.map((e) => (
          <MenuItem key={e.id} value={e.id}>
            {e.titulo} {e.status === 'finalizado' ? '(finalizado)' : '(aberto)'}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <Restaurant sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Agroeconomia - Relatórios</Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>{user?.nome}</Typography>
          <IconButton color="inherit" onClick={logoutAdmin}><Logout /></IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>Relatórios</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Paper sx={{ mb: 2 }}>
          <Tabs value={aba} onChange={(e, v) => setAba(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Cadastro de Produtores" />
            <Tab label="Detalhamento do Fornecimento" />
            <Tab label="Resumo para Pagamento de NFs" />
          </Tabs>
        </Paper>

        {aba === 0 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                label="Buscar por nome"
                value={filtroNome}
                onChange={(e) => setFiltroNome(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') carregarProdutores(filtroNome) }}
              />
              <Button variant="outlined" startIcon={<Search />} onClick={() => carregarProdutores(filtroNome)}>Buscar</Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" startIcon={<TableChart />} onClick={exportarProdutoresExcel} disabled={produtores.length === 0}>Excel</Button>
              <Button variant="outlined" color="secondary" startIcon={<PictureAsPdf />} onClick={exportarProdutoresPdf} disabled={produtores.length === 0}>PDF</Button>
            </Box>

            {loadingProdutores ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>CPF</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Telefone</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Localidade/Referência</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Grupo/Associação</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Gêneros Produzidos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {produtores.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.nome}</TableCell>
                        <TableCell>{p.cpf}</TableCell>
                        <TableCell>{p.telefone}</TableCell>
                        <TableCell>{p.localidadereferencia}</TableCell>
                        <TableCell>{p.nome_grupo || '-'}</TableCell>
                        <TableCell>{p.generos_produzidos || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {produtores.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">Nenhum produtor encontrado.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {aba === 1 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {seletorEdital}
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" startIcon={<TableChart />} onClick={exportarDetalhamentoExcel} disabled={!editalId || detalhamentoPivot.fornecedores.length === 0}>Excel</Button>
              <Button variant="outlined" color="secondary" startIcon={<PictureAsPdf />} onClick={exportarDetalhamentoPdf} disabled={!editalId || detalhamentoPivot.fornecedores.length === 0}>PDF</Button>
            </Box>

            {!editalId ? (
              <Alert severity="info">Selecione um edital para ver o detalhamento do fornecimento.</Alert>
            ) : loadingFornecimento ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : detalhamentoPivot.fornecedores.length === 0 ? (
              <Alert severity="info">Este edital ainda não possui atribuições finalizadas de produtor/quantidade.</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Grupo ou agricultor</TableCell>
                      {detalhamentoPivot.produtos.map((p) => (
                        <TableCell key={p} align="right" sx={{ fontWeight: 'bold' }}>{p}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalhamentoPivot.fornecedores.map((f) => (
                      <TableRow key={f.key}>
                        <TableCell>{f.nome}</TableCell>
                        {detalhamentoPivot.produtos.map((p) => (
                          <TableCell key={p} align="right">
                            {detalhamentoPivot.matrix[f.key]?.[p] ? formatNumeroBR(detalhamentoPivot.matrix[f.key][p]) : ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {aba === 2 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {seletorEdital}
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" startIcon={<TableChart />} onClick={exportarResumoExcel} disabled={!editalId || resumoPivot.fornecedores.length === 0}>Excel</Button>
              <Button variant="outlined" color="secondary" startIcon={<PictureAsPdf />} onClick={exportarResumoPdf} disabled={!editalId || resumoPivot.fornecedores.length === 0}>PDF</Button>
            </Box>

            {!editalId ? (
              <Alert severity="info">Selecione um edital para ver o resumo de pagamento de NFs.</Alert>
            ) : loadingFornecimento ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : resumoPivot.fornecedores.length === 0 ? (
              <Alert severity="info">Este edital ainda não possui atribuições finalizadas de produtor/quantidade.</Alert>
            ) : (
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan={2} sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Gênero</TableCell>
                      <TableCell rowSpan={2} align="right" sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Total (Kg)</TableCell>
                      {resumoPivot.fornecedores.map((f) => (
                        <TableCell key={f.key} colSpan={2} align="center" sx={{ fontWeight: 'bold' }}>{f.nome}</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      {resumoPivot.fornecedores.map((f) => (
                        <React.Fragment key={f.key}>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Kg</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>R$</TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumoPivot.produtos.map((p) => (
                      <TableRow key={p.nome}>
                        <TableCell>{p.nome}</TableCell>
                        <TableCell align="right">{formatNumeroBR(p.totalKg)}</TableCell>
                        {resumoPivot.fornecedores.map((f) => {
                          const cell = resumoPivot.matrix[f.key]?.[p.nome]
                          return (
                            <React.Fragment key={f.key}>
                              <TableCell align="right">{cell ? formatNumeroBR(cell.kg) : ''}</TableCell>
                              <TableCell align="right">{cell ? formatBRL(cell.valor) : formatBRL(0)}</TableCell>
                            </React.Fragment>
                          )
                        })}
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total NF</TableCell>
                      <TableCell />
                      {resumoPivot.fornecedores.map((f) => (
                        <React.Fragment key={f.key}>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatBRL(resumoPivot.totalPorFornecedor[f.key] || 0)}</TableCell>
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Container>
    </Box>
  )
}
